import * as Sentry from '@sentry/react';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '../../graphql';
import { waitUntilQuiet } from '../../performance/waitUntilQuiet';
import { useExtension } from '../useExtension';
import { teamContext, teamIdContext, userContext } from './team.context';
import type { Team } from './team.types';
import {
  getRecentTeamIdsStorageKey,
  parseStoredRecentTeamIds,
  upsertRecentTeamId,
} from './team.session.utils';

export function useMe() {
  let user = useContext(userContext);
  if (!user) user = {} as any;
  return user;
}

// Legacy ergonomics: most consumers destructure the team and only render
// below the session gate where it is resolved. The context itself models the
// unresolved/teamless states explicitly via `team: Team | null` + `teamState`.
const UNRESOLVED_TEAM = {} as Team;

export function useTeam() {
  const tenant = useContext(teamContext);

  return useMemo(
    () =>
      [
        tenant.team ?? UNRESOLVED_TEAM,
        tenant.refetchTeam,
        tenant.isFetchingTeam,
        tenant.queryKey,
        tenant.queryError,
      ] as const,
    [
      tenant.team,
      tenant.refetchTeam,
      tenant.isFetchingTeam,
      tenant.queryKey,
      tenant.queryError,
    ]
  );
}

export const useUiPermissionRoles = () =>
  useContext(teamContext).usesUiPermissionRoles;

export function useTeamRole() {
  const tenant = useContext(teamContext);

  if (tenant.usesUiPermissionRoles) {
    const permissionBundles = tenant.membership?.permissionBundles ?? [];
    const isPlatformAdmin = tenant.isSuperAdmin || tenant.isVrpAdmin;

    return {
      isTeamAdmin:
        isPlatformAdmin || permissionBundles.includes('ui:team-admin:v1'),
      isPartnerAdmin:
        isPlatformAdmin || permissionBundles.includes('ui:partner-admin:v1'),
      isTeamUser: permissionBundles.includes('ui:standard-member:v1'),
      isSuperAdmin: tenant.isSuperAdmin,
      isVrpAdmin: tenant.isVrpAdmin,
    };
  }

  return {
    isTeamAdmin: tenant.isTeamAdmin,
    isPartnerAdmin: tenant.isPartnerAdmin,
    isTeamUser: tenant.membership?.role === 'user',
    isSuperAdmin: tenant.isSuperAdmin,
    isVrpAdmin: tenant.isVrpAdmin,
  };
}

/** Membership of the authenticated user on the resolved team (from `/me`). */
export function useTeamMembership() {
  return useContext(teamContext).membership;
}

/** Issue counts for the resolved team (from `/me`), used for badges. */
export function useTeamIssueCounts() {
  return useContext(teamContext).issues;
}

export function usePartnerRegion() {
  const context = useContext(teamContext);

  return {
    managedTeamRegions: context.partnerContext?.managedTeamRegions ?? [],
    selectedDataRegion: context.selectedDataRegion,
    setSelectedDataRegion: context.setSelectedDataRegion,
  };
}

export function useTeamSwitch() {
  const { sendMessage } = useExtension();
  const goto = useNavigate();
  const queryClient = useQueryClient();
  const tenant = useContext(teamIdContext);
  const { id: userId, type: userType } = useMe();

  const setTeamId: (value: string | null, path?: string) => Promise<void> =
    useCallback(
      async (teamId: string | null, path?: string) => {
        const switchSpan =
          teamId && teamId !== tenant?.teamId
            ? Sentry.startInactiveSpan({
                name: 'write.team.switch',
                op: 'ui.write',
                attributes: {
                  'team.id': teamId,
                  portal: userType === 'owner' ? 'owner' : 'pm',
                  'write.action': 'team.switch',
                },
              })
            : undefined;

        if (teamId !== tenant?.teamId) {
          const shouldRefetch = (query: {
            options?: { meta?: Record<string, unknown> };
          }) => query.options?.meta?.teamScoped !== false;
          await queryClient
            .cancelQueries({ type: 'active', predicate: shouldRefetch })
            .catch(() => undefined);
        }

        if (teamId && userId) {
          const storageKey = getRecentTeamIdsStorageKey(userId);
          const recentTeamIds = parseStoredRecentTeamIds(
            globalThis.localStorage?.getItem(storageKey) ?? null
          );
          const nextRecentTeamIds = upsertRecentTeamId({
            teamId,
            recentTeamIds,
          });

          globalThis.localStorage?.setItem(
            storageKey,
            JSON.stringify(nextRecentTeamIds)
          );
        }

        tenant?.setTeamId(teamId);

        const isLinkSharing = window.location.search?.includes('sti=');

        if (!isLinkSharing && !path) {
          if (
            window.location.pathname.startsWith('/reports/statement-summaries')
          ) {
            goto('/reports/statement-summaries', { replace: true });
          } else if (window.location.pathname.includes('statement')) {
            goto('/statements', { replace: true });
          } else if (window.location.pathname === '/reservations') {
            goto('/reservations', { replace: true });
          } else if (window.location.pathname === '/activity') {
            goto('/activity', { replace: true });
          } else if (window.location.pathname.startsWith('/reconciliation')) {
            goto('/reconciliation');
          } else if (window.location.pathname.startsWith('/deposit')) {
            goto('/deposits');
          } else if (window.location.pathname.startsWith('/expense')) {
            goto('/expenses');
          } else if (window.location.pathname.startsWith('/listing')) {
            goto('/listings');
          } else if (window.location.pathname.startsWith('/contact')) {
            goto('/contacts');
          } else if (window.location.pathname.startsWith('/owner')) {
            goto('/owners');
          } else if (window.location.pathname.startsWith('/connection')) {
            goto('/connections');
          } else if (window.location.pathname.startsWith('/fees-commission')) {
            goto('/fees-commissions');
          } else if (window.location.pathname.startsWith('/dashboard')) {
            goto('/');
          } else if (
            new URLSearchParams(window.location.search).has(
              'activityRecordId'
            ) ||
            new URLSearchParams(window.location.search).has('activityEvent')
          ) {
            const search = new URLSearchParams(window.location.search);
            [
              'activityEvent',
              'activityFrom',
              'activityRecordType',
              'activityRecordId',
              'activityType',
              'activityOperation',
              'activityActor',
              'activityAccounting',
              'activityUpdated',
              'activityChanges',
            ].forEach((key) => search.delete(key));
            goto(
              { pathname: window.location.pathname, search: search.toString() },
              { replace: true }
            );
          }
        }

        if (path) {
          goto(path);
        }

        // The switch is done when the new team's first queries have settled.
        // The quiet window is long because the team context resolves first.
        if (switchSpan) {
          waitUntilQuiet({
            isBusy: () =>
              queryClient.isFetching({
                predicate: (query) => query.state.data === undefined,
              }) > 0,
            quietMs: 300,
            subscribe: (listener) =>
              queryClient.getQueryCache().subscribe(listener),
            timeoutMs: 20_000,
          }).then((result) => {
            if (result !== 'quiet') {
              switchSpan.setStatus({ code: 2, message: 'cancelled' });
            }
            switchSpan.end();
          });
        }

        try {
          sendMessage({ message: 'team_changed', data: { teamId } });
        } catch (error) {
          console.error(error);
        }
      },
      [
        goto,
        sendMessage,
        tenant?.teamId,
        userId,
        userType,
        queryClient,
        tenant?.setTeamId,
      ]
    );

  return setTeamId;
}
